import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMapContext } from '@hero/web-context';
import { LocationCard } from './LocationCard';
import { ChevronsDown, Edit2 } from 'lucide-react';
import { Button } from '@hero/web-components';

export interface LocationCardsProps {
  /** Position of the cards panel */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

/**
 * LocationCards - Right-side panel showing location cards with smooth zoom on click
 * Uses MapContext as the single source of truth for locations, editing state, and handlers
 */
export function LocationCards({ position = 'top-left' }: LocationCardsProps) {
  const ctx = useMapContext();
  const { t } = useTranslation();

  const origin = ctx.locations[0] || null;
  const destinations = ctx.locations.slice(1);

  const isSingleLocationMode = ctx.locationMode === 'single';

  const positionClasses = {
    'top-left': 'top-16 left-4',
    'top-right': 'top-16 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
  };

  // In single location mode, show placeholder card if no location exists
  // In multi mode, don't render if no locations
  if (!origin && destinations.length === 0) {
    if (!isSingleLocationMode) {
      return null;
    }
    // For single location mode, show placeholder card - render it below
  }

  return (
    <div
      className={`absolute ${
        positionClasses[position]
      } z-20 w-[280px] max-h-[calc(100vh-80px)] overflow-y-auto pe-2`}
      style={{
        scrollbarWidth: 'thin',
        scrollbarColor: '#cbd5e1 transparent',
      }}>
      {/* Origin Card or Placeholder Card (for single location mode) */}
      {origin ? (
        <>
          <LocationCard
            location={origin}
            index={0}
            isOrigin={true}
            onCardClick={() => ctx.handleCardClick(0)}
            onEdit={() => ctx.handleLocationEdit(0, true)}
            onDone={() => ctx.handleLocationDone(0, true)}
            onCancel={() => ctx.handleLocationCancel(0, true)}
            onSaveLocation={() => {
              if (ctx.handleSaveLocationRequested && origin.cords) {
                ctx.handleSaveLocationRequested(origin.cords, 0);
              }
            }}
            onEditSavedLocation={
              origin.savedLocation?._id && ctx.saveLocationState?.handleEditSavedLocationRequested
                ? () =>
                    ctx.saveLocationState!.handleEditSavedLocationRequested!(
                      origin.savedLocation!._id!,
                      0
                    )
                : undefined
            }
            onAccurateLocationToggle={(isAccurate, patch) =>
              ctx.handleAccurateLocationToggle(0, true, isAccurate, patch)
            }
          />

          {/* Connector with dotted line and arrow after origin */}
          {destinations.length > 0 && (
            <div className="flex justify-center">
              <div className="relative flex flex-col items-center">
                <div className="w-0 h-2 border-s border-dotted border-gray-400" />
                <ChevronsDown className="size-5 text-gray-700 -my-1" strokeWidth={2.5} />
                <div className="w-0 h-2 border-s border-dotted border-gray-400" />
              </div>
            </div>
          )}
        </>
      ) : isSingleLocationMode ? (
        <div className="group relative p-3 bg-white/20 backdrop-blur-md rounded-lg border border-gray-200/50 shadow-sm">
          <div className="flex flex-col">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0 text-start">
                <p className="text-sm font-semibold text-gray-500 mb-1.5">
                  {t('map.noLocation', 'No location set')}
                </p>
                <p className="text-xs text-gray-400 mb-3">
                  {t('map.clickEditToAdd', 'Click edit to add a location')}
                </p>
              </div>
              {ctx.mode !== 'view' && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="bg-white border rounded-full flex-shrink-0 h-8 w-8"
                  onClick={e => {
                    e.stopPropagation();
                    ctx.handleLocationEdit(0, true);
                  }}>
                  <Edit2 className="w-4 h-4 text-gray-500" />
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Destination Cards */}
      {destinations.map((destination, index) => {
        const actualIndex = index + 1; // +1 because origin is at index 0
        return (
          <React.Fragment key={`destination-${index}`}>
            <LocationCard
              location={destination}
              index={actualIndex}
              isOrigin={false}
              onCardClick={() => ctx.handleCardClick(actualIndex)}
              onEdit={() => ctx.handleLocationEdit(actualIndex, false)}
              onDone={() => ctx.handleLocationDone(actualIndex, false)}
              onCancel={() => ctx.handleLocationCancel(actualIndex, false)}
              onSaveLocation={() => {
                if (ctx.handleSaveLocationRequested && destination.cords) {
                  ctx.handleSaveLocationRequested(destination.cords, actualIndex);
                }
              }}
              onEditSavedLocation={
                destination.savedLocation?._id &&
                ctx.saveLocationState?.handleEditSavedLocationRequested
                  ? () =>
                      ctx.saveLocationState!.handleEditSavedLocationRequested!(
                        destination.savedLocation!._id!,
                        actualIndex
                      )
                  : undefined
              }
              onAccurateLocationToggle={(isAccurate, patch) =>
                ctx.handleAccurateLocationToggle(actualIndex, false, isAccurate, patch)
              }
            />

            {/* Connector with dotted line and arrow between destinations */}
            {index < destinations.length - 1 && (
              <div className="flex justify-center">
                <div className="relative flex flex-col items-center">
                  <div className="w-0 h-2 border-s border-dotted border-gray-400" />
                  <ChevronsDown className="size-5 text-gray-700 -my-1" strokeWidth={2.5} />
                  <div className="w-0 h-2 border-s border-dotted border-gray-400" />
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
